export const DEFAULT_API_BASE =
  "https://ciphernest-vault-api.fourie-jacques777.workers.dev";

export type TokenResponse = {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  Key?: string;
  PrivateKey?: string | null;
  Kdf?: number;
  KdfIterations?: number;
  KdfMemory?: number | null;
  KdfParallelism?: number | null;
  UserDecryptionOptions?: {
    MasterPasswordUnlock?: {
      MasterKeyEncryptedUserKey?: string;
      MasterKeyWrappedUserKey?: string;
      Salt?: string;
      Kdf?: {
        KdfType?: number;
        Iterations?: number;
        Memory?: number | null;
        Parallelism?: number | null;
      };
    };
  };
};

export type PreloginResponse = {
  kdf: number;
  kdfIterations: number;
  kdfMemory: number | null;
  kdfParallelism: number | null;
};

export type SyncResponse = {
  object: string;
  profile: Record<string, unknown>;
  folders: Array<Record<string, unknown>>;
  collections: Array<Record<string, unknown>>;
  ciphers: Array<Record<string, unknown>>;
  userDecryption?: Record<string, unknown>;
};

export class CipherNestApiClient {
  constructor(public baseUrl: string = DEFAULT_API_BASE) {
    this.baseUrl = baseUrl.replace(/\/$/, "");
  }

  private async request<T>(
    path: string,
    init: RequestInit & { token?: string } = {}
  ): Promise<T> {
    const headers = new Headers(init.headers);
    if (!headers.has("Accept")) headers.set("Accept", "application/json");
    if (init.token) headers.set("Authorization", `Bearer ${init.token}`);
    if (init.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    const res = await fetch(`${this.baseUrl}${path}`, { ...init, headers });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      let message = text;
      try {
        const j = JSON.parse(text) as { message?: string; error?: string };
        message = j.message || j.error || text;
      } catch {
        /* raw */
      }
      throw new Error(message || `HTTP ${res.status}`);
    }
    if (res.status === 204) return undefined as T;
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) return (await res.json()) as T;
    return undefined as T;
  }

  getConfig() {
    return this.request<Record<string, unknown>>("/api/config");
  }

  prelogin(email: string) {
    return this.request<PreloginResponse>("/api/accounts/prelogin", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  async loginPassword(opts: {
    email: string;
    masterPasswordHash: string;
    deviceName?: string;
    deviceIdentifier?: string;
    twoFactorToken?: string;
    twoFactorProvider?: string;
  }): Promise<TokenResponse> {
    const body = new URLSearchParams();
    body.set("grant_type", "password");
    body.set("username", opts.email);
    body.set("password", opts.masterPasswordHash);
    body.set("scope", "api offline_access");
    body.set("client_id", "web");
    body.set("deviceType", "10");
    body.set(
      "deviceIdentifier",
      opts.deviceIdentifier || crypto.randomUUID()
    );
    body.set("deviceName", opts.deviceName || "CipherNest Web");
    if (opts.twoFactorToken) {
      body.set("twoFactorToken", opts.twoFactorToken);
      body.set("twoFactorProvider", opts.twoFactorProvider || "0");
    }

    const res = await fetch(`${this.baseUrl}/identity/connect/token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=utf-8",
        Accept: "application/json",
      },
      body,
    });
    if (!res.ok) {
      const text = await res.text();
      let err: Error & { twoFactor?: boolean; raw?: string } = new Error(
        text || `Login failed (${res.status})`
      );
      try {
        const j = JSON.parse(text) as {
          error_description?: string;
          TwoFactorProviders?: number[];
        };
        err = new Error(j.error_description || text) as Error & {
          twoFactor?: boolean;
          raw?: string;
        };
        if (
          j.TwoFactorProviders ||
          /two factor/i.test(j.error_description || "")
        ) {
          err.twoFactor = true;
        }
      } catch {
        /* raw */
      }
      err.raw = text;
      throw err;
    }
    return (await res.json()) as TokenResponse;
  }

  async refresh(refreshToken: string): Promise<TokenResponse> {
    const body = new URLSearchParams();
    body.set("grant_type", "refresh_token");
    body.set("refresh_token", refreshToken);
    body.set("client_id", "web");
    const res = await fetch(`${this.baseUrl}/identity/connect/token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=utf-8",
        Accept: "application/json",
      },
      body,
    });
    if (!res.ok) throw new Error("Session expired. Please sign in again.");
    return (await res.json()) as TokenResponse;
  }

  sync(token: string) {
    return this.request<SyncResponse>("/api/sync", { token });
  }

  /** Milliseconds since epoch; cheap check before full vault sync. */
  async revisionDate(token: string): Promise<number> {
    const res = await fetch(`${this.baseUrl}/api/accounts/revision-date`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "text/plain",
      },
    });
    if (!res.ok) throw new Error(`Revision check failed (${res.status})`);
    const text = (await res.text()).trim();
    const ms = Number(text);
    return Number.isFinite(ms) ? ms : Date.now();
  }

  createCipher(token: string, cipher: Record<string, unknown>) {
    return this.request<Record<string, unknown>>("/api/ciphers", {
      method: "POST",
      token,
      body: JSON.stringify(cipher),
    });
  }

  updateCipher(token: string, id: string, cipher: Record<string, unknown>) {
    return this.request<Record<string, unknown>>(`/api/ciphers/${id}`, {
      method: "PUT",
      token,
      body: JSON.stringify(cipher),
    });
  }

  deleteCipher(token: string, id: string, soft = true) {
    return this.request<void>(
      `/api/ciphers/${id}${soft ? "" : "?soft=false"}`,
      { method: "DELETE", token }
    );
  }

  restoreCipher(token: string, id: string) {
    return this.request<Record<string, unknown>>(`/api/ciphers/${id}/restore`, {
      method: "PUT",
      token,
    });
  }

  listTrash(token: string) {
    return this.request<{ data: Array<Record<string, unknown>> }>(
      "/api/ciphers/trash",
      { token }
    );
  }

  createFolder(token: string, name: string) {
    return this.request<Record<string, unknown>>("/api/folders", {
      method: "POST",
      token,
      body: JSON.stringify({ name }),
    });
  }

  updateFolder(token: string, id: string, name: string) {
    return this.request<Record<string, unknown>>(`/api/folders/${id}`, {
      method: "PUT",
      token,
      body: JSON.stringify({ name }),
    });
  }

  deleteFolder(token: string, id: string) {
    return this.request<void>(`/api/folders/${id}`, {
      method: "DELETE",
      token,
    });
  }

  createOrganization(
    token: string,
    body: Record<string, unknown>
  ) {
    return this.request<Record<string, unknown>>("/api/organizations", {
      method: "POST",
      token,
      body: JSON.stringify(body),
    });
  }

  inviteOrgUsers(
    token: string,
    orgId: string,
    emails: string[],
    accessAll = true
  ) {
    return this.request<Record<string, unknown>>(
      `/api/organizations/${orgId}/users/invite`,
      {
        method: "POST",
        token,
        body: JSON.stringify({ emails, type: 2, accessAll }),
      }
    );
  }

  confirmOrgUser(
    token: string,
    orgId: string,
    memberId: string,
    key: string
  ) {
    return this.request<Record<string, unknown>>(
      `/api/organizations/${orgId}/users/${memberId}/confirm`,
      {
        method: "POST",
        token,
        body: JSON.stringify({ key }),
      }
    );
  }

  listOrgUsers(token: string, orgId: string) {
    return this.request<{ data: Array<Record<string, unknown>> }>(
      `/api/organizations/${orgId}/users`,
      { token }
    );
  }

  orgPublicKeys(token: string, orgId: string, ids: string[] = []) {
    return this.request<{ data: Array<{ id: string; userId: string; key: string | null }> }>(
      `/api/organizations/${orgId}/users/public-keys`,
      {
        method: "POST",
        token,
        body: JSON.stringify({ ids }),
      }
    );
  }

  updateAccountKeys(
    token: string,
    publicKey: string,
    encryptedPrivateKey: string
  ) {
    return this.request<Record<string, unknown>>("/api/accounts/keys", {
      method: "PUT",
      token,
      body: JSON.stringify({
        publicKey,
        privateKey: encryptedPrivateKey,
        keys: {
          publicKeyEncryptionKeyPair: {
            publicKey,
            wrappedPrivateKey: encryptedPrivateKey,
          },
        },
      }),
    });
  }

  register(body: Record<string, unknown>) {
    return this.request<Record<string, unknown>>("/api/accounts/register", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  listCollections(token: string, orgId: string) {
    return this.request<{ data: Array<Record<string, unknown>> }>(
      `/api/organizations/${orgId}/collections`,
      { token }
    );
  }

  createCollection(token: string, orgId: string, name: string) {
    return this.request<Record<string, unknown>>(
      `/api/organizations/${orgId}/collections`,
      {
        method: "POST",
        token,
        body: JSON.stringify({ name }),
      }
    );
  }

  importCiphers(token: string, body: Record<string, unknown>) {
    return this.request<{ imported?: number; data?: unknown[] }>(
      "/api/ciphers/import",
      { method: "POST", token, body: JSON.stringify(body) }
    );
  }

  verifyPassword(token: string, masterPasswordHash: string) {
    return this.request<Record<string, unknown>>("/api/accounts/verify-password", {
      method: "POST",
      token,
      body: JSON.stringify({ masterPasswordHash }),
    });
  }

  changePassword(
    token: string,
    body: {
      masterPasswordHash: string;
      newMasterPasswordHash: string;
      key: string;
      masterPasswordHint?: string | null;
    }
  ) {
    return this.request<Record<string, unknown>>("/api/accounts/password", {
      method: "POST",
      token,
      body: JSON.stringify(body),
    });
  }

  setupTwoFactor(token: string) {
    return this.request<{ secret: string; otpauth: string; enabled: boolean }>(
      "/api/accounts/two-factor/authenticator/setup",
      { method: "POST", token, body: "{}" }
    );
  }

  enableTwoFactor(
    token: string,
    opts: { secret: string; code: string; masterPasswordHash: string }
  ) {
    return this.request<Record<string, unknown>>(
      "/api/accounts/two-factor/authenticator",
      { method: "POST", token, body: JSON.stringify(opts) }
    );
  }

  disableTwoFactor(token: string, masterPasswordHash: string) {
    return this.request<Record<string, unknown>>(
      "/api/accounts/two-factor/authenticator",
      {
        method: "DELETE",
        token,
        body: JSON.stringify({ masterPasswordHash }),
      }
    );
  }

  listSends(token: string) {
    return this.request<{ data: Array<Record<string, unknown>> }>("/api/sends", {
      token,
    });
  }

  createSend(token: string, body: Record<string, unknown>) {
    return this.request<Record<string, unknown>>("/api/sends", {
      method: "POST",
      token,
      body: JSON.stringify(body),
    });
  }

  deleteSend(token: string, id: string) {
    return this.request<void>(`/api/sends/${id}`, { method: "DELETE", token });
  }

  listAttachments(token: string, cipherId: string) {
    return this.request<{ data: Array<Record<string, unknown>> }>(
      `/api/ciphers/${cipherId}/attachment`,
      { token }
    );
  }

  createAttachment(
    token: string,
    cipherId: string,
    body: Record<string, unknown>
  ) {
    return this.request<Record<string, unknown>>(
      `/api/ciphers/${cipherId}/attachment`,
      { method: "POST", token, body: JSON.stringify(body) }
    );
  }

  getAttachment(token: string, cipherId: string, attId: string) {
    return this.request<Record<string, unknown>>(
      `/api/ciphers/${cipherId}/attachment/${attId}`,
      { token }
    );
  }

  deleteAttachment(token: string, cipherId: string, attId: string) {
    return this.request<void>(
      `/api/ciphers/${cipherId}/attachment/${attId}`,
      { method: "DELETE", token }
    );
  }
}
